import type { AuthInfo } from "../api/types";
import { isPersistedInLocalStorage, readPersistedValue, writePersistedValue } from "../../../../../../shared/frontend/auth/authProfileStorage";

// Portal- und Plattform-Admin-Sitzung leben in getrennten Storage-Scopes, damit beide
// parallel nutzbar sind (siehe shared/plans/admin-login-vereinheitlichung.md).
// Portal behält die historischen Keys, damit bestehende Sitzungen erhalten bleiben.
const SCOPE_KEYS = {
  portal: { token: "authToken", info: "authInfo" },
  admin: { token: "adminAuthToken", info: "adminAuthInfo" }
} as const;
const LEGACY_AUTH_KEY = "m1member.auth";
const AUTH_COOKIE_NAME = "m1member_authToken";

export type AuthScope = "portal" | "admin";

export function getCurrentAuthScope(): AuthScope {
  // Hash-Router: der Admin-Bereich (inkl. /admin/login) liegt unter #/admin.
  if (typeof window !== "undefined" && window.location.hash.startsWith("#/admin")) {
    return "admin";
  }
  return "portal";
}

function parseAuthInfo(raw: string | null): AuthInfo | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthInfo;
  } catch {
    return null;
  }
}

function loadLegacyAuth(): { token: string; info: AuthInfo | null } {
  const raw = typeof localStorage === "undefined" ? null : localStorage.getItem(LEGACY_AUTH_KEY);
  if (!raw) return { token: "", info: null };
  try {
    const parsed = JSON.parse(raw) as { token?: string | null; info?: AuthInfo | null };
    return {
      token: String(parsed?.token ?? "").trim(),
      info: parsed?.info ?? null
    };
  } catch {
    return { token: "", info: null };
  }
}

function readAuthCookie(): string {
  if (typeof document === "undefined") return "";
  const prefix = `${AUTH_COOKIE_NAME}=`;
  const parts = document.cookie.split(";");
  for (const part of parts) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return "";
}

export function loadAuthFromStorage(scope: AuthScope = getCurrentAuthScope()): { token: string; info: AuthInfo | null; persist: boolean } {
  const keys = SCOPE_KEYS[scope];
  const persist = isPersistedInLocalStorage(keys.token) || isPersistedInLocalStorage(keys.info);
  const token = readPersistedValue(keys.token) || "";
  const info = parseAuthInfo(readPersistedValue(keys.info));
  if (token || info) return { token, info, persist };

  if (scope === "portal") {
    const legacy = loadLegacyAuth();
    if (legacy.token || legacy.info) {
      saveAuthToStorage(legacy, "portal", true);
      return { ...legacy, persist: true };
    }
  }

  const cookieToken = readAuthCookie();
  if (cookieToken) return { token: cookieToken, info: null, persist: false };

  return { token: "", info: null, persist: false };
}

export function loadAuthInfoFromStorage(scope: AuthScope = getCurrentAuthScope()): AuthInfo | null {
  const info = parseAuthInfo(readPersistedValue(SCOPE_KEYS[scope].info));
  if (info) return info;
  return scope === "portal" ? loadLegacyAuth().info : null;
}

export function saveAuthToStorage(
  auth: { token: string; info: AuthInfo | null },
  scope: AuthScope = getCurrentAuthScope(),
  persist?: boolean
) {
  const keys = SCOPE_KEYS[scope];
  writePersistedValue(keys.token, auth.token || null, persist);
  writePersistedValue(keys.info, auth.info ? JSON.stringify(auth.info) : null, persist);

  if (scope === "portal" && typeof localStorage !== "undefined") {
    localStorage.removeItem(LEGACY_AUTH_KEY);
  }
}

export function isAuthPersistent(scope: AuthScope = getCurrentAuthScope()): boolean {
  return isPersistedInLocalStorage(SCOPE_KEYS[scope].token);
}
